<template>
  <div>
    <div class="c-bar__cell" :class="targetClass" v-click-outside="hideSelector">
      <div class="c-bar__cell-inner" :class="open ? 'active' : ''" @click.prevent="open = !open">
        <div class="c-bar__cell-body">
          <div class="c-bar__text">
            <div class="c-bar__title">{{ title }}</div>
            <div class="c-bar__placeholder">
              {{ selectedLabel.length >= 1 ? selectedLabel.join('、') : '請選擇' }}
            </div>
          </div>
          <div class=""><i class="fas fa-angle-down"></i></div>
        </div>
      </div>

      <component v-bind:is="type" :open="open" :title="title" @close="close">
        <slot>
          <div>
            <cascader-panel
              v-model="value"
              :options="options"
              :border="false"
              :props="{ multiple: multiple, emitPath: emitPath, checkStrictly: true, expandTrigger: 'hover' }"
              ref="currentCascader"
              @change="setValue"></cascader-panel>

            <div class="d-flex  align-items-center border-top c-bar__action" :class="max ? 'justify-content-between' : 'justify-content-end'">
              <div class="me-5" v-if="max"
                :class="selectedValue.length === max ? 'text-danger' : ''">
                已選擇 {{ selectedValue.length }} / {{ max }}
              </div>
              <div class="d-flex align-items-center">
                <a href="javascript://"
                  class="text-primary fw-medium me-3"
                  @click="clearSelector()"
                >
                  清除
                </a>
                <a href="javascript://"
                  class="btn btn-primary c-bar__action-btn"
                  @click.stop.prevent="open=!open"
                >
                  確定
                </a>
              </div>
            </div>
          </div>
        </slot>
      </component>
    </div>
  </div>
</template>

<script>
const { reactive, toRefs, watch, onMounted, onUnmounted, ref, onUpdated } = Vue;
import dropdown from './wrapper/dropdown';
import modal from './wrapper/modal';
import CascaderPanel from 'element-ui/lib/cascader-panel';
import mobileCheck from '../../../services/utilities';

export default {
  name: 'Selector',
  components: {
    dropdown,
    modal,
    CascaderPanel,
  },
  props: {
    title: String,
    multiple: Boolean,
    targetOptions: Array,
    targetClass: String,
    selectedValue: Array,
    max: Number,
    modalOpen: {
      type: Boolean,
      default: false,
    },
    useTree: {
      type: Boolean,
      default: false,
    },
    emitPath: {
      type: Boolean,
      default: true,
    },
    treeRef: Object
  },
  setup(props, ctx) {
    const currentCascader = ref(null);
    const options = props.targetOptions;
    const state = reactive({
      isMobile: false,
      type: String,
      open: props.modalOpen,
      value: props.selectedValue,
      selectedLabel: []
    });

    mobileCheck(state);

    watch(() => props.treeRef, (treeRef) => {
      if (treeRef) {
        currentCascader.value = treeRef;

        fillIndeterminatesForMobileTree(treeRef, treeRef.getCheckedNodes());

        setLabel();
      }
    });

    function fillIndeterminatesForMobileTree(treeRef, nodes) {
      for (const node of nodes) {
        const treeNode = treeRef.getNode(node.value);

        if (treeNode.parent && !Array.isArray(treeNode.parent.data)) {
          disabledParent(treeNode.parent);
        }

        if (treeNode.childNodes && treeNode.childNodes.length) {
          disabledChildren(treeNode);
        }

        treeNode.loaded = false;
        treeNode.expand();
      }
    }

    onMounted(async () => {
      changeType();
    });

    onUpdated(() => {
      if (!state.selectedLabel.length && state.value.length && !(state.isMobile && props.useTree)) {
        setLabel();
      }

      if (state.value.length && !(state.isMobile && props.useTree) && currentCascader.value) {
        setMultipleValue();
      }
    });

    watch(() => state.isMobile, () => {
      changeType();
    });

    watch(() => state.value, (newVal) => {
      ctx.emit('change', newVal);
    });

    const setValue = () => {
      setMultipleValue();

      setLabel();
    };

    const setLabel = () => {
      state.selectedLabel = [];

      currentCascader.value.getCheckedNodes().map((item) => {
        if (Array.isArray(item.pathLabels)) {
          state.selectedLabel.push(item.pathLabels.join(''));
        } else {
          state.selectedLabel.push(item.label);
        }
      });
    };

    function setTreeSelectedLabel(labels) {
      state.selectedLabel = labels;
    }

    ctx.expose({ setTreeSelectedLabel });

    const setMultipleValue = async () => {
      // disable 綁定在 data上 indeterminate 是綁定在 cascader 的node 上 所以要分別跑map 去清空
      clearOptionDisabled(options);

      currentCascader.value.$children.map((e) => {
        clearOptionIndeterminate(e.nodes);
      });

      let count;

      if (props.max) {
        count = currentCascader.value.getCheckedNodes().length < props.max
          ? currentCascader.value.getCheckedNodes().length
          : props.max;
      } else {
        count = currentCascader.value.getCheckedNodes().length;
      }

      for (let i = 0; i < count; i++) {
        let node = currentCascader.value.getCheckedNodes()[i];
        if (!node.checked) {
          return;
        }

        if (node.children.length) {
          await disabledChildren(node);
        }

        if (node.parent) {
          disabledParent(node.parent);
        }
      }

      if (props.max) {
        await checkMaxChecked(props.max, options);
      }
    };

    const checkMaxChecked = (max, nodes) => {
      if (state.value.length >= max) {
        const selectedArray = [];

        currentCascader.value.getCheckedNodes().map((e) => {
          selectedArray.push(e.value);
        });

        nodes.map(async (e) => {
          e.disabled = selectedArray.indexOf(e.value) === -1;

          if (e.hasOwnProperty('children')) {
            await checkMaxChecked(max, e.children);
          }
        });
      }
    };

    const disabledChildren = (node) => {
      const children = node.children || node.childNodes;

      children.forEach((e) => {
        e.data.disabled = true;
        e.indeterminate = true;

        if (e.children && e.children.length) {
          disabledChildren(e);
        }

        if (e.childNodes && e.childNodes.length) {
          disabledChildren(e);
        }
      });
    };

    const disabledParent = (node) => {
      node.data.disabled = true;
      node.indeterminate = true;

      if (node.parent && !Array.isArray(node.parent.data)) {
        disabledParent(node.parent);
      }
    };

    const clearOptionDisabled = (options) => {
      options.map((e, i) => {
        e.disabled = false;

        if (e.hasOwnProperty('children')) {
          clearOptionDisabled(e.children);
        }
      });
    };

    const clearOptionIndeterminate = (nodes) => {
      nodes.map((e) => {
        e.indeterminate = false;

        if (e.hasOwnProperty('children')) {
          clearOptionIndeterminate(e.children);
        }
      });
    };

    const hideSelector = () => {
      state.open = false;
    };

    const clearSelector = () => {
      currentCascader.value.clearCheckedNodes();
    };

    const changeType = () => {
      state.isMobile
        ? state.type = 'modal'
        : state.type = 'dropdown';
    };

    const close = () => {
      state.open = false;
    };

    return {
      ...toRefs(state),
      currentCascader,
      options,

      // Function
      setValue,
      clearSelector,
      hideSelector,
      close,
    };
  }
};
</script>

<style scoped>

</style>
